import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { useMutation } from '@apollo/react-hooks'

import SettingOption from './setting-option'
import styles from './settings-panel.css'
import PROFILE_PREFERENCES_MUTATION from '../../../../../../graphql/mutations/update-profile-preferences.gql'

/**
 * Renders settings options for video playback and autoplay preferences in the player.
 *
 * Key Features:
 * - Displays autoplay preferences with on/off options
 * - Shows available video quality options
 * - Handles both quality and autoplay state synchronization
 * - Auto-closes panel when quality or autoplay selection completes
 */
const SettingsTable = ({
  isSettingsPanelOpen,
  playbackQualityOptions,
  showQualityUpgradePopup,
  currentQuality,
  toggleSettingsPanelOpen,
  setQuality,
  nextEpisodeAutoplayEnabled,
  isSafari,
  isEpisode
}) => {
  const [setAutoplay] = useMutation(PROFILE_PREFERENCES_MUTATION)
  const [selectedQualityOption, setSelectedQualityOption] = useState(currentQuality)
  const [selectedAutoplayOption, setSelectedAutoplayOption] = useState(nextEpisodeAutoplayEnabled)
  const [isDisabled, setIsDisabled] = useState(false)
  const prevQualityRef = useRef(currentQuality)
  const prevAutoplayRef = useRef(selectedAutoplayOption)

  /**
   * Synchronize autoplay selection when prop changes
   * Updates UI to reflect latest profile preference fetched from server
   */
  useEffect(() => {
    setSelectedAutoplayOption(nextEpisodeAutoplayEnabled)
  }, [nextEpisodeAutoplayEnabled])

  // Auto-close settings panel when quality preference change completes
  useEffect(() => {
    if (prevQualityRef.current !== currentQuality) {
      toggleSettingsPanelOpen()
    }
    prevQualityRef.current = currentQuality
  }, [currentQuality, toggleSettingsPanelOpen])

  // Auto-close settings panel when autoplay preference change completes
  useEffect(() => {
    if (prevAutoplayRef.current !== selectedAutoplayOption) {
      toggleSettingsPanelOpen()
    }
    prevAutoplayRef.current = selectedAutoplayOption
  }, [selectedAutoplayOption, toggleSettingsPanelOpen])

  /**
   * Handles video quality selection
   * Updates quality in parent, disables UI during async operation,
   * then re-enables when the change is processed
   */
  const handleQualityOnClick = (option) => {
    setSelectedQualityOption(option)
    setDisable()
    setQuality(option).then(() => {
      setIsDisabled(false)
    })
  }

  /**
   * Handles autoplay preference toggle
   * Sends GraphQL mutation to update profile preferences,
   * disables UI during save, then re-enables when complete
   */
  const handleAutoplayOnClick = (option) => {
    setSelectedAutoplayOption(option.value)
    setDisable()
    const mutateProfilePreferencesInput = {
      input: {
        nextEpisodeAutoplayEnabled: option.value
      }
    }

    setAutoplay({ variables: mutateProfilePreferencesInput }).then(() => {
      setIsDisabled(false)
    })
  }

  // Disables the settings table to prevent interaction during async operations
  const setDisable = () => {
    setIsDisabled(true)
  }

  // Autoplay option configurations with descriptions
  const AutoplayOptions = [{
    label: 'Off',
    description: '',
    value: false
  },
  {
    label: 'On',
    description: 'Automatically play the next episode when the credits begin',
    value: true
  }]

  return (
    <div
      className={classnames(styles.wrapper, {
        [styles.hide]: !isSettingsPanelOpen,
        [styles.tableDisabled]: isDisabled
      })}
      onClick={evt => {
        evt.stopPropagation()
      }}
      onDoubleClick={evt => {
        evt.stopPropagation()
      }}
    >
      {/* Hide Autoplay next episode for Movie and Trailer */}
      {isEpisode && selectedAutoplayOption !== undefined && (
        <>
          <div className={styles.tableTitle}>Autoplay next episode</div>
          <table className={styles.table}>
            <tbody>
              {AutoplayOptions.map(option => {
                return (
                  <SettingOption
                    option={option}
                    isActive={option.value === selectedAutoplayOption}
                    key={option.value}
                    setDisable={setDisable}
                    onClick={() => handleAutoplayOnClick(option)}
                  />
                )
              })}
            </tbody>
          </table>
        </>
      )}

      {/* Hide Video Quality on Safari because data saver is not supported there */}
      {!isSafari && (
        <>
          <div className={styles.tableTitle}>Video Quality</div>
          <table className={styles.table}>
            <tbody>
              {playbackQualityOptions.wifi.map(option => {
                return (
                  <SettingOption
                    option={option}
                    isActive={option.id === selectedQualityOption.id}
                    key={option.id}
                    setDisable={setDisable}
                    onClick={() => handleQualityOnClick(option)}
                    showQualityUpgradePopup={showQualityUpgradePopup}
                  />
                )
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  )
}

const QualityOptionPropType = PropTypes.shape({
  id: PropTypes.string.isRequired
})

SettingsTable.propTypes = {
  isSettingsPanelOpen: PropTypes.bool.isRequired,
  playbackQualityOptions: PropTypes.shape({
    wifi: PropTypes.arrayOf(QualityOptionPropType.isRequired).isRequired
  }).isRequired,
  currentQuality: QualityOptionPropType.isRequired,
  toggleSettingsPanelOpen: PropTypes.func.isRequired,
  setQuality: PropTypes.func.isRequired,
  showQualityUpgradePopup: PropTypes.func,
  nextEpisodeAutoplayEnabled: PropTypes.bool,
  isSafari: PropTypes.bool.isRequired,
  isEpisode: PropTypes.bool.isRequired
}

SettingsTable.defaultProps = {
  showQualityUpgradePopup: undefined,
  nextEpisodeAutoplayEnabled: undefined
}

export default SettingsTable
